import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./SelectNextPlayer.css";
import { useScribbleLobby } from "../../hooks/useScribbleLobby";
import { useLobbyWords } from "../../hooks/useLobbyWords";
import { wordOptionsForTurn } from "../../lib/scribbleGame";
import { startTabooRound } from "../../lib/tabooGame";
import { fetchLeaderboard } from "../../functions/fetchLeaderboard";
import Swal from "sweetalert2";

const PersonIcon = () => (
  <svg className="snp-person-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

export default function SelectNextPlayer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedId, setSelectedId] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [starting, setStarting] = useState(false);
  const [leaders, setLeaders] = useState([]);

  const { lobbyName, players, isHost, phase, lobby, joining, configured } = useScribbleLobby({
    autoJoin: true,
  });
  const turnIndex = Number(lobby?.game?.turnIndex) || 0;
  const game = lobby?.game || {};
  const overallTimerRemaining = Math.max(0, Number(game.overallTimerRemaining) || 0);
  const overallTimerTotal = Math.max(0, Number(game.overallTimerTotal) || 0);
  const { questions, timeDurationMin, loading: wordsLoading } = useLobbyWords(lobbyName);

  const storedUserData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const sessionId = storedUserData.sessionId;
  const organizationId = storedUserData.organizationId;
  const token = sessionId && organizationId ? `${sessionId}&${organizationId}` : "";

  useEffect(() => {
    document.documentElement.classList.add("taboo-game-static-bg");
    document.body.classList.add("taboo-game-static-bg");
    return () => {
      document.documentElement.classList.remove("taboo-game-static-bg");
      document.body.classList.remove("taboo-game-static-bg");
    };
  }, []);

  useEffect(() => {
    if (!isHost && configured && !joining && phase !== "select_player") {
      navigate("/game", { replace: true, state: { lobby_name: lobbyName, ...(location.state || {}) } });
    }
  }, [isHost, configured, joining, phase, navigate, lobbyName, location.state]);

  useEffect(() => {
    if (phase === "playing" || phase === "completed") {
      navigate("/game", { replace: true, state: { lobby_name: lobbyName, ...(location.state || {}) } });
    }
  }, [phase, navigate, lobbyName, location.state]);

  useEffect(() => {
    if (overallTimerRemaining === 0 && overallTimerTotal > 0 && phase === "select_player") {
      navigate("/leaderboard", { replace: true, state: { lobby_name: lobbyName, ...(location.state || {}) } });
    }
  }, [overallTimerRemaining, overallTimerTotal, phase, navigate, lobbyName, location.state]);

  useEffect(() => {
    if (!lobbyName || !token || !sessionId || !organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchLeaderboard({ token, sessionId, organizationId, lobby_name: lobbyName });
        if (cancelled) return;
        const key = lobbyName.trim();
        const sorted = Array.isArray(data)
          ? [...data]
              .filter((row) => String(row.lobby_name ?? "").trim() === key)
              .sort((a, b) => (Number(b.total_score) || 0) - (Number(a.total_score) || 0))
          : [];
        setLeaders(sorted);
      } catch {
        if (!cancelled) setLeaders([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lobbyName, token, sessionId, organizationId]);

  const scoreByUserId = useMemo(() => {
    const map = new Map();
    leaders.forEach((row, i) => {
      const id = String(row.userId ?? row.userid ?? row.id ?? "");
      if (id) map.set(id, { points: Number(row.total_score) || 0, rank: i + 1 });
    });
    return map;
  }, [leaders]);

  const roster = useMemo(() => {
    const list = players.filter((p) => !p.isHost);
    const withScores = list.map((p) => {
      const id = String(p.id);
      return {
        ...p,
        points: scoreByUserId.get(id)?.points ?? 0,
      };
    });
    withScores.sort((a, b) => b.points - a.points || String(a.name).localeCompare(String(b.name)));
    return withScores.map((p, i) => ({
      ...p,
      rank: i + 1,
    }));
  }, [players, scoreByUserId]);

  const correctGuesserIdsSet = useMemo(() => {
    const raw = lobby?.game?.lastRoundCorrectGuessers || {};
    if (!raw || typeof raw !== "object") return new Set();
    return new Set(Object.keys(raw).map(String));
  }, [lobby?.game?.lastRoundCorrectGuessers]);

  const selectedPlayer = roster.find((p) => p.id === selectedId);

  const cardForTurn = useMemo(() => {
    const opts = wordOptionsForTurn(questions, turnIndex, 1);
    return opts[0] || null;
  }, [questions, turnIndex]);

  const handleConfirm = async () => {
    if (overallTimerRemaining === 0 && overallTimerTotal > 0) {
      await Swal.fire("Time's up!", "The game timer has expired.", "info");
      return;
    }
    if (!selectedId || !lobbyName || !cardForTurn) {
      await Swal.fire("Select player", "Choose a player and ensure cards are loaded.", "warning");
      return;
    }
    setStarting(true);
    try {
      const timerSec = Math.max(1, Number(timeDurationMin) || 1) * 60;
      await startTabooRound(lobbyName, {
        playerId: selectedId,
        word: cardForTurn.word,
        tabooWords:
          cardForTurn.taboo?.length >= 3
            ? cardForTurn.taboo
            : String(cardForTurn.hint || "")
                .split(/[,·|]/)
                .map((s) => s.trim())
                .filter(Boolean)
                .slice(0, 3),
        timerSeconds: timerSec,
        turnIndex,
      });
      navigate("/game", { replace: true, state: { lobby_name: lobbyName, ...(location.state || {}) } });
    } catch (e) {
      await Swal.fire("Error", e?.message || "Could not start round.", "error");
    } finally {
      setStarting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <main className="snp-page" aria-label="Select next player">
      <div className="tg-confetti" aria-hidden="true" />
      {overallTimerTotal > 0 && (
        <div style={{ 
          textAlign: 'center', 
          fontSize: '1.2rem', 
          fontWeight: 'bold', 
          color: overallTimerRemaining <= 30 ? '#ef476f' : '#ffd166',
          marginBottom: '1rem',
          padding: '0.5rem'
        }}>
          Time Remaining: {formatTime(overallTimerRemaining)} (Paused)
        </div>
      )}
      <div className="snp-inner">
        <section className="snp-leaderboard" aria-label="Player list">
          <div className="snp-table-head">
            <span>No.</span>
            <span>Name</span>
            <span>Points</span>
          </div>
          {roster.length === 0 ? (
            <p style={{ textAlign: "center", fontWeight: 700 }}>Waiting for players…</p>
          ) : (
            <div className="snp-player-rows">
              {roster.map((p) => {
                const isCorrect = correctGuesserIdsSet.has(String(p.id));
                const rowClass = `snp-table-row${isCorrect ? " snp-table-row--correct" : ""}${
                  isHost ? " snp-table-row--pick" : ""
                }${selectedId === p.id && isHost ? " snp-table-row--selected" : ""}`;

                if (!isHost) {
                  return (
                    <div
                      key={p.id}
                      className={rowClass}
                      aria-label={`${p.name} ${p.points} points`}
                    >
                      <span>{p.rank}</span>
                      <span>{p.name}</span>
                      <span>{p.points}</span>
                    </div>
                  );
                }

                return (
                  <button
                    key={p.id}
                    type="button"
                    className={rowClass}
                    onClick={() => {
                      setSelectedId(p.id);
                      setShowPicker(false);
                    }}
                  >
                      <span>{p.rank}</span>
                    <span>{p.name}</span>
                    <span>{p.points}</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {isHost ? (
          <section className="snp-next-card" aria-label="Next player">
            <h2 className="snp-next-title">Next</h2>
            <PersonIcon />
            <p className="snp-next-status">
              {selectedPlayer ? selectedPlayer.name : "No player selected"}
            </p>
            <button
              type="button"
              className="snp-select-btn"
              onClick={() => {
                if (selectedId && !wordsLoading && cardForTurn) {
                  void handleConfirm();
                } else {
                  setShowPicker((v) => !v);
                }
              }}
              disabled={starting || roster.length === 0}
            >
              {starting ? "Starting…" : selectedId ? "Start round" : "Select"}
            </button>
            {showPicker && !selectedId && (
              <div className="snp-picker">
                {roster.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="snp-picker-item"
                    onClick={() => {
                      setSelectedId(p.id);
                      setShowPicker(false);
                    }}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </section>
        ) : (
          <section className="snp-next-card" aria-label="Waiting for host">
            <h2 className="snp-next-title">Waiting</h2>
            <p className="snp-next-status">Host is selecting the next player…</p>
          </section>
        )}
      </div>
    </main>
  );
}
